import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Apple, Wallet, Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { UnionJack } from "@/components/UnionJack";
import { useBasket } from "@/context/BasketContext";
import { createOrder, getProducts, formatPrice, multibuyRate } from "@/lib/api";

const FREE_SHIPPING = 40;
const SHIPPING_COST = 4.95;

export default function Checkout() {
    const { items, subtotal, savings, clearBasket, addItem } = useBasket();
    const [suggestions, setSuggestions] = useState([]);
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        address: "",
        city: "",
        postcode: "",
        country: "United Kingdom",
        gift_note: "",
    });

    const shipping = subtotal >= FREE_SHIPPING ? 0 : SHIPPING_COST;
    const total = subtotal + shipping;

    useEffect(() => {
        getProducts()
            .then((all) => {
                const inBasket = new Set(items.map((i) => i.id));
                setSuggestions(all.filter((p) => !inBasket.has(p.id)).slice(0, 3));
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (items.length === 0) return;
        setSubmitting(true);
        try {
            const { gift_note, ...customer } = form;
            const order = await createOrder({
                items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
                customer,
                gift_note: gift_note || null,
            });
            clearBasket();
            navigate(`/order/${order.id}`);
        } catch (err) {
            toast.error("We couldn't place your order. Please check your details and try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (items.length === 0) {
        return (
            <main className="bg-[#F6F1E7] min-h-screen pt-40 pb-24 text-center" data-testid="checkout-empty">
                <h1 className="font-serif-display text-4xl">Your basket is empty.</h1>
                <p className="text-sm text-black/50 mt-3">Add something extraordinary first.</p>
                <Link to="/shop" className="btn-luxe btn-green mt-8 inline-flex" data-testid="checkout-empty-shop-link">
                    Shop Collection
                </Link>
            </main>
        );
    }

    const inputClass =
        "w-full bg-white/60 border border-black/12 px-4 py-3.5 text-sm rounded-sm placeholder:text-black/30 focus:outline-none focus:border-[#0F2A1F] transition-colors";

    return (
        <main className="bg-[#F6F1E7] min-h-screen pt-28 sm:pt-36 pb-24" data-testid="checkout-page">
            <div className="max-w-[1080px] mx-auto px-5 sm:px-8 lg:px-12">
                <Reveal className="text-center mb-12">
                    <p className="eyebrow text-[#8C6A3D] mb-4">Fast &amp; Secure</p>
                    <h1 className="font-serif-display text-5xl font-medium">Checkout</h1>
                </Reveal>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Form */}
                    <Reveal className="lg:col-span-7">
                        {/* Express (demo) */}
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            <button
                                type="button"
                                onClick={() => toast("Express payment available at launch", { description: "Please use standard checkout below." })}
                                className="flex items-center justify-center gap-2 bg-[#0B0B0C] text-white py-3.5 rounded-sm text-sm font-semibold hover:bg-[#232326] transition-colors"
                                data-testid="checkout-apple-pay-button"
                            >
                                <Apple size={16} /> Pay
                            </button>
                            <button
                                type="button"
                                onClick={() => toast("Express payment available at launch", { description: "Please use standard checkout below." })}
                                className="flex items-center justify-center gap-2 bg-white border border-black/15 py-3.5 rounded-sm text-sm font-semibold hover:border-black/40 transition-colors"
                                data-testid="checkout-google-pay-button"
                            >
                                <Wallet size={16} /> Google Pay
                            </button>
                        </div>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex-1 h-px bg-black/10" />
                            <span className="font-mono-meta text-[9px] tracking-[0.25em] text-black/35">OR CONTINUE BELOW</span>
                            <div className="flex-1 h-px bg-black/10" />
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="checkout-form">
                            <div className="grid grid-cols-2 gap-4">
                                <input required placeholder="First name" value={form.first_name} onChange={set("first_name")} className={inputClass} data-testid="checkout-first-name-input" />
                                <input required placeholder="Last name" value={form.last_name} onChange={set("last_name")} className={inputClass} data-testid="checkout-last-name-input" />
                            </div>
                            <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputClass} data-testid="checkout-email-input" />
                            <input required placeholder="Address" value={form.address} onChange={set("address")} className={inputClass} data-testid="checkout-address-input" />
                            <div className="grid grid-cols-2 gap-4">
                                <input required placeholder="City" value={form.city} onChange={set("city")} className={inputClass} data-testid="checkout-city-input" />
                                <input required placeholder="Postcode" value={form.postcode} onChange={set("postcode")} className={inputClass} data-testid="checkout-postcode-input" />
                            </div>
                            <input required placeholder="Country" value={form.country} onChange={set("country")} className={inputClass} data-testid="checkout-country-input" />
                            <textarea
                                placeholder="Gift note (optional) — we'll hand-write it on a card"
                                value={form.gift_note}
                                onChange={set("gift_note")}
                                rows={3}
                                className={inputClass}
                                data-testid="checkout-gift-note-input"
                            />

                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn-luxe btn-green w-full !py-4 disabled:opacity-60"
                                data-testid="checkout-place-order-button"
                            >
                                <Lock size={13} />
                                {submitting ? "Placing Order…" : `Place Order — ${formatPrice(total)}`}
                            </button>
                            <p className="font-mono-meta text-[9px] text-black/35 text-center tracking-wide">
                                DEMO CHECKOUT — NO PAYMENT WILL BE TAKEN
                            </p>
                        </form>
                    </Reveal>

                    {/* Summary */}
                    <Reveal className="lg:col-span-5" delay={0.1}>
                        <div className="bg-[#0B0B0C] text-[#F6F1E7] rounded-xl p-7 sticky top-28" data-testid="checkout-summary">
                            <h2 className="font-serif-display text-2xl font-medium mb-6">Order Summary</h2>
                            <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
                                {items.map((item) => (
                                    <div key={item.id} className="flex items-center gap-4" data-testid={`checkout-item-${item.id}`}>
                                        <div
                                            className="w-10 h-14 rounded-sm flex-shrink-0 border border-white/10"
                                            style={{ background: `linear-gradient(180deg, #F3EEE3 72%, ${item.band_color || "#0F2A1F"} 72%)` }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-serif-display text-sm font-medium truncate">{item.name}</p>
                                            <p className="font-mono-meta text-[9px] text-white/40">QTY {item.quantity}</p>
                                        </div>
                                        <span className="text-sm text-[#C9B37A]">{formatPrice(item.price * item.quantity)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="border-t border-white/10 mt-6 pt-5 space-y-2.5 text-sm">
                                {savings > 0 && (
                                    <div className="flex justify-between text-[#C9B37A]">
                                        <span>Multi-buy savings</span>
                                        <span data-testid="checkout-savings">−{formatPrice(savings)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-white/60">
                                    <span>Subtotal</span>
                                    <span data-testid="checkout-subtotal">{formatPrice(subtotal)}</span>
                                </div>
                                <div className="flex justify-between text-white/60">
                                    <span>Delivery</span>
                                    <span data-testid="checkout-shipping">{shipping === 0 ? "Complimentary" : formatPrice(shipping)}</span>
                                </div>
                                <div className="flex justify-between items-baseline pt-3 border-t border-white/10">
                                    <span className="eyebrow text-white/50">Total</span>
                                    <span className="font-serif-display text-2xl font-medium" data-testid="checkout-total">{formatPrice(total)}</span>
                                </div>
                            </div>
                            <p className="font-mono-meta text-[9px] text-white/25 mt-5 tracking-wide text-center">
                                EVERY BATCH TRACEABLE · SMALL BATCH CRAFTED · 100% BRITISH
                            </p>
                        </div>

                        {/* Cross-sell — complete the order */}
                        {suggestions.length > 0 && (
                            <div className="mt-8 border border-black/10 bg-white/40 rounded-xl p-6" data-testid="checkout-cross-sell">
                                <div className="flex items-baseline justify-between gap-4 mb-5">
                                    <h3 className="font-serif-display text-xl font-medium">Complete your order</h3>
                                    <Link
                                        to="/shop"
                                        className="font-mono-meta text-[9px] tracking-[0.18em] text-[#0F2A1F] hover:text-[#8C6A3D] transition-colors flex items-center gap-1"
                                        data-testid="checkout-view-all-link"
                                    >
                                        VIEW ALL <ArrowRight size={11} />
                                    </Link>
                                </div>
                                <div className="space-y-4">
                                    {suggestions.map((p) => (
                                        <div key={p.id} className="flex items-center gap-4" data-testid={`checkout-suggestion-${p.slug}`}>
                                            {p.category === "cut" ? (
                                                <div
                                                    className="w-10 h-14 rounded-sm flex-shrink-0 border border-black/10"
                                                    style={{ background: `linear-gradient(180deg, #F3EEE3 72%, ${p.band_color} 72%)` }}
                                                />
                                            ) : (
                                                <img src={p.images[0]} alt={p.name} loading="lazy" className="w-10 h-14 rounded-sm object-cover flex-shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <Link to={`/product/${p.slug}`} className="font-serif-display text-sm font-medium truncate block hover:text-[#0F2A1F] transition-colors">
                                                    {p.name}
                                                </Link>
                                                <p className="font-mono-meta text-[9px] text-black/45 mt-0.5">{formatPrice(p.price)}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => addItem(p, 1, { openDrawer: false })}
                                                className="flex items-center gap-1.5 border border-black/15 rounded-sm px-3 py-2 font-mono-meta text-[9px] tracking-[0.15em] hover:bg-[#0F2A1F] hover:text-[#F6F1E7] hover:border-[#0F2A1F] transition-colors"
                                                data-testid={`checkout-suggestion-add-${p.slug}`}
                                            >
                                                <Plus size={11} /> ADD
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Provenance assurance */}
                        <div className="mt-6 flex items-start gap-3 px-1" data-testid="checkout-provenance-note">
                            <UnionJack width={18} muted className="mt-0.5" />
                            <p className="font-mono-meta text-[9px] tracking-[0.15em] text-black/45 leading-relaxed">
                                100% BRITISH BEEF, RAISED ON NAMED BRITISH FARMS. TRADITIONALLY PROCESSED — NEVER HALAL.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </main>
    );
}
